<template>
  <div class="user-dashboard-container">
    <transition name="fade" mode="out-in">
      <div v-if="userData">

        <b-card no-body class="mb-3 border-0 shadow-sm">
          <b-card-body class="p-3">
            <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-between">
              <div class="d-flex align-items-center mb-3 mb-md-0">
                <div class="mr-3 position-relative flex-shrink-0">
                  <img class="img-thumbnail"
                       style=""
                       :src="'https://character.pokemonrevolution.net/' + style()"/>
                  <span :class="userData.online ? 'bg-success' : 'bg-danger'"
                        class="position-absolute border border-white rounded-circle"
                        style="width: 15px; height: 15px; bottom: 0; right: 0;"></span>
                </div>

                <div style="min-width: 0;">
                  <h4 class="mb-0 font-weight-bold">
                    {{ userData.username }}
                    <span class="text-muted h6 font-weight-normal">#{{ userData.id }}</span>
                  </h4>
                  <div class="mt-1 d-flex flex-wrap align-items-center">
                    <b-badge v-if="userData.security_id in roles" variant="primary" class="mr-1 mb-1">
                      {{ roles[userData.security_id].name }}
                    </b-badge>
                    <b-badge v-if="isActuallyBanned" variant="danger" class="mr-1 mb-1" :title="userData.banInfo">
                      <i class="fa fa-ban mr-1"></i> {{ banTypeLabel(userData.banned) }}
                    </b-badge>
                    <b-badge v-if="servers[currentServer || 'silver'].readonly" variant="warning" class="mr-1 mb-1">
                      Read-only
                    </b-badge>

                    <b-badge v-if="userData.former_usernames && userData.former_usernames.length"
                             variant="secondary"
                             class="border mb-1"
                             style="white-space: normal; text-align: left;"
                             :title="'History: ' + userData.former_usernames.join(', ')">
                      <i class="fa fa-history"></i> Formerly: {{ userData.former_usernames.join(', ') }}
                    </b-badge>
                  </div>
                </div>
              </div>

              <div class="d-flex align-items-center">
                <b-button-group class="flex-wrap">
                  <b-button
                    v-for="(srv, key) in filteredServers"
                    :key="key"
                    :variant="currentServer === key ? getServerVariant(key) : 'outline-secondary'"
                    @click="switchServer(key)"
                    :active="currentServer === key"
                    size="sm"
                    class="server-btn"
                  >
                    {{ capitalizeFirstLetter(key) }}
                  </b-button>
                </b-button-group>
              </div>
            </div>
          </b-card-body>
        </b-card>

        <b-alert
          v-if="hasPermission('MacroView') && currentMacroStats && currentMacroStats.count > 0"
          show
          variant="danger"
          class="border-0 shadow-sm mb-3"
        >
          <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
              <div class="mr-3">
                <i class="fa fa-warning fa-2x"></i>
              </div>
              <div>
                <h5 class="alert-heading mb-1 font-weight-bold uppercase letter-spacing-1">Macro Usage Detected</h5>
                <p class="mb-1">
                  This user has triggered <strong>{{ formatNumber(currentMacroStats.count) }}</strong> macro events.
                </p>
                <p class="mb-0 small opacity-80">
                  <strong>Most used keys:</strong> {{ currentMacroStats.keys }}
                </p>
              </div>
            </div>
            <b-button variant="outline-danger" size="sm" @click="openMacroDetails" class="px-3 font-weight-bold">
              <i class="fa fa-search mr-1"></i> VIEW DETAILS
            </b-button>
          </div>
        </b-alert>

        <b-card no-body :class="['shadow-sm', 'dashboard-card', isUpdatingServer ? 'is-loading-server' : '']">
          <b-overlay :show="isUpdatingServer" variant="transparent" blur="3px" rounded="sm">
            <template #overlay>
              <div class="text-center">
                <b-spinner variant="primary" class="mb-2"></b-spinner>
                <div class="font-weight-bold text-primary">Switching Server...</div>
              </div>
            </template>

            <div class="card-header border-bottom-0">
               <draggable v-model="tabsList" tag="ul" class="nav nav-tabs card-header-tabs" group="tabs" ghost-class="ghost-tab" handle=".drag-handle" @end="onDragEnd">
                  <li class="nav-item sortable-tab" v-for="tab in tabsList" :key="tab.id">
                      <a href="#" class="nav-link d-flex align-items-center" :class="{active: currentTabId === tab.id}" @click.prevent="selectTab(tab.id)">
                          <i class="fa fa-ellipsis-v mr-2 drag-handle" title="Drag to reorder"></i>
                          <i :class="[tab.icon, 'mr-1']"></i> {{tab.title}}
                          <span v-if="tab.id === 'identity' && hasCriticalRisk" class="badge badge-pill badge-danger ml-1">!</span>
                      </a>
                  </li>
               </draggable>
            </div>

            <div class="card-body">
              <div class="tab-content">
                <div v-for="tab in tabsList" :key="tab.id" v-if="visitedTabs.includes(tab.id)" v-show="currentTabId === tab.id" class="tab-pane active">
                  <b-overlay :show="tab.loading ? tab.loading() : false" variant="transparent" opacity="0.8" blur="1px">
                    <div v-if="tab.wrapper === 'b-card'" class="card">
                       <component
                        :is="tab.component"
                        v-bind="resolveProps(tab.props)"
                        @refresh="tab.events && tab.events.refresh ? tab.events.refresh() : null"
                      />
                    </div>
                    <component v-else
                      :is="tab.component"
                      v-bind="resolveProps(tab.props)"
                      @refresh="tab.events && tab.events.refresh ? tab.events.refresh() : null"
                    />
                  </b-overlay>
                </div>
              </div>
            </div>

          </b-overlay>
        </b-card>

    </div>
      <div v-else key="loading" class="d-flex align-items-center justify-content-center" style="min-height: 400px;">
        <div class="text-center">
          <b-spinner variant="primary" type="grow" style="width: 3rem; height: 3rem;" class="mb-3"></b-spinner>
          <h5 class="text-muted">Loading user profile...</h5>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
// Component Imports
import UserProfile from './base/index'
import ConnectionsView from './connections/index'
import InvestigationView from './investigation/index'
import ApprovedDevicesView from './approved/index'
import InventoryView from './inventory/index'
import PokedexView from './pokedex/index'
import TradeLogView from './tradelog/index'
import PvPLogView from './pvplog/index'
import HistoryView from './history/index'
import GuildView from './guild/index'
import VariablesView from './variables/index'
import UserMails from './mails/index'
import SnapshotsView from './snapshots/index'
import PokemonListView from '../pokemon/index' // Imported Pokemon List Component

// Newly added components for separate tabs
import StaffRewards from './base/StaffRewards.vue'
import Forum from './base/Forum'

import Servers from '../../_servers'
import Roles from '../../_roles'
import moment from 'moment'
import { mapGetters, mapActions } from 'vuex'
import draggable from 'vuedraggable'

export default {
  name: 'UserUnifiedDashboard',
  components: {
    UserProfile,
    ConnectionsView,
    InvestigationView,
    ApprovedDevicesView,
    InventoryView,
    PokedexView,
    UserMails,
    PokemonListView,
    HistoryView,
    TradeLogView,
    PvPLogView,
    VariablesView,
    SnapshotsView,
    StaffRewards,
    Forum,
    GuildView,
    draggable
  },
  data() {
    return {
      servers: Servers,
      roles: Roles,
      // Default to what's in URL or null (to allow backend auto-detection)
      currentServer: this.$route.query.server || null,
      isReady: false,
      isUpdatingServer: false,
      macroDetailsLoading: false,
      userTabOrder: [],
      tabsList: [],
      visitedTabs: [],
      currentTabId: 'profile'
    }
  },
  computed: {
    ...mapGetters({
      userData: 'userData',
      authUser: 'authUser', // Needed for permissions
      userLock: 'userLock',  // Needed for form disabling in StaffRewards/Forum
      sharedIPs: 'sharedIPs',
      allConnections: 'allConnections', // Correct getter for connections tab
      allInventory: 'allInventory',
      pokedex: 'pokedex',
      allPokemon: 'allPokemon',
      Tradelogs: 'Tradelogs',
      PVPlogs: 'PVPlogs',
      userHistory: 'userHistory',
      guildhistory: 'guildhistory',
      variables: 'variables',
      userMails: 'userMails',
      userServers: 'userServers',
      macroStats: 'macroStats',
      macroDetails: 'macroDetails'
    }),
    currentMacroStats() {
      if (this.userData && this.macroStats) {
        return this.macroStats[this.userData.username]
      }
      return null
    },
    hasCriticalRisk() {
      // Implement critical risk logic or map from Vuex
      return false
    },
    isActuallyBanned() {
      if (!this.userData || !this.userData.banned || this.userData.banned === '') return false;
      if (this.userData.banned === 'P') return true;

      const bannedUntilStr = this.userData.banned_until && typeof this.userData.banned_until === 'object'
        ? this.userData.banned_until.String
        : this.userData.banned_until;

      if (bannedUntilStr && bannedUntilStr !== '' && !bannedUntilStr.startsWith('0001')) {
        return moment(bannedUntilStr).isAfter(moment());
      }
      return true;
    },
    filteredServers() {
      // Use the master keys from the servers object to preserve the original order from _servers.js
      const masterKeys = Object.keys(this.servers);
      const filtered = {};

      masterKeys.forEach(serverName => {
        // Only include if the user is on this server (or if no specific user-server data is loaded yet)
        const userHasData = !this.userServers || this.userServers.length === 0 || this.userServers.includes(serverName);
        if (userHasData) {
          filtered[serverName] = this.servers[serverName];
        }
      });
      return filtered;
    },
    availableTabs() {
      if (!this.userData) return [];
      const tabs = [];

      // 1. Profile
      tabs.push({
        id: 'profile',
        title: 'Profile',
        icon: 'icon-user',
        component: 'UserProfile',
        props: { userData: this.userData, server: this.currentServer },
        events: { refresh: this.fetchUserData }
      });

      // 2. History
      if (this.hasPermission('UserInfractionView')) {
        tabs.push({
          id: 'history',
          title: 'History',
          icon: 'fa fa-history',
          component: 'HistoryView',
          props: { username: this.userData.username },
          loading: () => this.userHistory === null
        });
      }

      // 3. Inventory
      if (this.hasPermission('InventoryView')) {
        tabs.push({
          id: 'inventory',
          title: 'Inventory',
          icon: 'icon-bag',
          component: 'InventoryView',
          props: { username: this.userData.username, server: this.currentServer, readonly: this.servers[this.currentServer || 'silver'].readonly },
          loading: () => this.allInventory === null
        });
      }

      // 4. Mails
      if (this.hasPermission('UserMailsView') && !this.servers[this.currentServer || 'silver'].readonly) {
        tabs.push({
          id: 'mails',
          title: 'Mails',
          icon: 'fa fa-envelope',
          component: 'UserMails',
          props: { username: this.userData.username, server: this.currentServer },
          loading: () => this.userMails === null
        });
      }

      // 5. Variables
      if (this.hasPermission('VariablesView')) {
        tabs.push({
          id: 'variables',
          title: 'Variables',
          icon: 'fa fa-database',
          component: 'VariablesView',
          props: { username: this.userData.username, server: this.currentServer, readonly: this.servers[this.currentServer || 'silver'].readonly },
          loading: () => this.variables === null
        });
      }

      // 6. Pokedex
      if (this.hasPermission('ViewPokedex')) {
        tabs.push({
          id: 'pokedex',
          title: 'Pokedex',
          icon: 'icon-ghost',
          component: 'PokedexView',
          props: { username: this.userData.username, server: this.currentServer },
          loading: () => this.pokedex === null
        });
      }

      // 7. Pokemon
      if (this.hasPermission('PokemonView')) {
        tabs.push({
          id: 'pokemon',
          title: 'Pokemon',
          icon: 'fa fa-list',
          component: 'PokemonListView',
          props: { fixedUser: this.userData.username, lockedServer: this.currentServer },
          loading: () => this.allPokemon === null
        });
      }

      // 8. Identity
      if (this.hasPermission('ConnectionsView')) {
        tabs.push({
          id: 'identity',
          title: 'Identity',
          icon: 'fa fa-user-secret text-danger',
          component: 'ConnectionsView',
          props: { username: this.userData.username, server: this.currentServer, mode: 'identity' },
          loading: () => this.allConnections === null
        });
      }

      // 9. Approved Devices
      if (this.hasPermission('ApprovedDevicesView')) {
        tabs.push({
          id: 'authorized',
          title: 'Authorized Devices',
          icon: 'fa fa-mobile-phone',
          component: 'ApprovedDevicesView',
          props: { username: this.userData.username, server: this.currentServer },
          loading: () => !this.userData
        });
      }

      // 10. Guild History
      if (this.hasPermission('UserGuildHistoryView')) {
        tabs.push({
          id: 'guilds',
          title: 'Guild History',
          icon: 'fa fa-users',
          component: 'GuildView',
          props: { username: this.userData.username, server: this.currentServer },
          loading: () => this.guildhistory === null
        });
      }

      // 11. Trade Log
      if (this.hasPermission('TradelogsView')) {
        tabs.push({
          id: 'trade',
          title: 'Trade Log',
          icon: 'fa fa-exchange',
          component: 'TradeLogView',
          props: { username: this.userData.username, server: this.currentServer },
          loading: () => this.Tradelogs === null
        });
      }

      // 12. PvP Log
      if (this.hasPermission('PVPlogView')) {
        tabs.push({
          id: 'pvp',
          title: 'PvP Log',
          icon: 'fa fa-trophy',
          component: 'PvPLogView',
          props: { username: this.userData.username, server: this.currentServer },
          loading: () => this.PVPlogs.data === null
        });
      }

      // 13. Snapshots
      if (this.hasPermission('ViewSnapshots')) {
        tabs.push({
          id: 'snapshots',
          title: 'Snapshots',
          icon: 'fa fa-camera',
          component: 'SnapshotsView',
          props: { username: this.userData.username }
        });
      }

      // 14. Staff Rewards
      tabs.push({
        id: 'rewards',
        title: 'Staff Rewards',
        icon: 'fa fa-gift',
        component: 'StaffRewards',
        props: { userData: this.userData, userLock: this.userLock, servers: this.servers },
        events: { refresh: this.fetchUserData },
        wrapper: 'b-card'
      });

      // 15. Forum
      if (this.authUser) {
         try {
           if (this.hasPermission('ForumGroupChange')) {
             tabs.push({
                id: 'forum',
                title: 'Forum',
                icon: 'fa fa-comments',
                component: 'Forum',
                props: { userData: this.userData.forum, userLock: this.userLock },
                wrapper: 'b-card'
             });
           }
         } catch(e) {}
      }

      return tabs;
    }
  },
    watch: {
      availableTabs: {
        immediate: true,
        handler() {
           this.updateTabsList();
        }
      },
      userServers(val) {
        // Only force a switch if we already have a selected server that is now invalid.
        // If currentServer is null, we wait for the backend to auto-detect the best server.
        if (this.currentServer && val && val.length > 0 && !val.includes(this.currentServer)) {
          this.switchServer(val[0]);
        }
      },
      '$route'(to, from) {
        const toUser = to.params.username;
        const fromUser = from.params.username;
        const toServer = to.query.server || null;
        const fromServer = from.query.server || null;
        const toTab = to.params.tab;
        const fromTab = from.params.tab;

        // 1. Handle User Change
        if (toUser !== fromUser) {
          this.clearUserData(); // Force loading state
          this.clearTabContentData();
          this.currentServer = toServer;
          this.fetchUserData();
          if (this.$store.getters.serversUsername !== toUser) {
            this.emit('getUserServers', toUser);
          }
          this.selectTab(toTab);
          return; // User change handled everything
        }

        // 2. Handle Server Change (Same User)
        if (toServer !== fromServer) {
          this.currentServer = toServer;
          this.isUpdatingServer = true;
          this.clearUserData(); // Clear main user data to force a fresh fetch
          this.clearTabContentData();
          this.fetchUserData();
        }

        // 3. Handle Tab Change (Same User, Same Server)
        if (toTab !== fromTab) {
          this.selectTab(toTab);
          this.fetchUserData();
        }
      },
      userData(val) {
        this.isUpdatingServer = false; // Stop loading overlay when data arrives

        if (val) {
          // Sync currentServer if it was auto-detected by backend (currentServer was null)
          // ONLY if it hasn't been set by URL yet
          if (!this.currentServer && val.server) {
            this.currentServer = val.server;
          }

          if (val.username && this.hasPermission('MacroView')) {
            const server = this.currentServer || 'gold'
            if (!this.isReadOnlyServer(server)) {
              this.emit('getMacroStats', { username: val.username, server: server })
            } else {
              this.$store.commit('setMacroStats', { username: val.username, keys: '', count: 0 })
            }
          }

          // Data loaded.
          this.isReady = false;
          this.$nextTick(() => {
            this.selectTab(this.$route.params.tab || 'profile');
            // Allow a small buffer for child components to register/update
            this.$nextTick(() => {
              this.isReady = true;
            });
          });
        }
      },
      macroDetails() {
        this.macroDetailsLoading = false
      }
    },
    created() {
      // Load tab order from localStorage
      const saved = localStorage.getItem('modtools_dashboard_tab_order');
      if (saved) {
          try {
              this.userTabOrder = JSON.parse(saved);
          } catch(e) {}
      }

      this.clearUserData()
      this.clearTabContentData()
      // Ensure currentServer matches URL on create
      if (this.$route.query.server) {
        this.currentServer = this.$route.query.server
      }
      this.fetchUserData()
      // Only fetch if we don't have them yet (e.g. from global search)
      if (this.$store.getters.serversUsername !== this.$route.params.username) {
        this.emit('getUserServers', this.$route.params.username)
      }
    },
    mounted() {
      this.$nextTick(() => {
        this.selectTab(this.$route.params.tab || 'profile');
        // If userData is already there (e.g. hot reload or cache), mark ready
        if (this.userData) {
           this.isReady = true;
           if (this.userData.username && this.hasPermission('MacroView')) {
             const server = this.currentServer || 'gold'
             if (!this.isReadOnlyServer(server)) {
               this.emit('getMacroStats', { username: this.userData.username, server: server })
             } else {
               this.$store.commit('setMacroStats', { username: this.userData.username, keys: '', count: 0 })
             }
           }
        }
      });
    },
    methods: {
      ...mapActions(['getUserData', 'clearUserData']),

      resolveProps(props) {
        // Since props are objects in my definition, just return them.
        // Vue reactivity should handle updates if they are references to data.
        return props;
      },

      selectTab(tabId) {
        let target = (tabId || 'profile').toLowerCase();

        // Fallback to profile if tab is not available or user doesn't have permission
        // ONLY if we have tabs to check against (otherwise we're still loading)
        if (this.tabsList.length > 0 && !this.tabsList.find(t => t.id === target)) {
            target = 'profile';
        }

        // Update visited tabs (lazy loading logic)
        if (!this.visitedTabs.includes(target)) {
            this.visitedTabs.push(target);
        }

        this.currentTabId = target;

        // Update URL if needed
        const currentParam = (this.$route.params.tab || 'profile').toLowerCase();
        if (this.tabsList.length > 0 && currentParam !== target) {
           this.$router.replace({
            name: 'User',
            params: { ...this.$route.params, tab: target },
            query: this.$route.query
          }).catch(() => {});
        }
      },

      updateTabsList() {
          if (!this.userTabOrder || this.userTabOrder.length === 0) {
              this.tabsList = [...this.availableTabs];
              return;
          }

          const sorted = [...this.availableTabs];
          sorted.sort((a, b) => {
             const idxA = this.userTabOrder.indexOf(a.id);
             const idxB = this.userTabOrder.indexOf(b.id);

             if (idxA === -1 && idxB === -1) return 0;
             if (idxA === -1) return 1;
             if (idxB === -1) return -1;

             return idxA - idxB;
          });
          this.tabsList = sorted;
      },

      onDragEnd() {
          this.userTabOrder = this.tabsList.map(t => t.id);
          localStorage.setItem('modtools_dashboard_tab_order', JSON.stringify(this.userTabOrder));
      },

      isReadOnlyServer(server) {
        const name = server || 'silver'
        return this.servers[name] && this.servers[name].readonly
      },

      clearTabContentData() {
        // Reset all tab-related store modules to null
        // Since modules are not namespaced, we call mutations directly
        const mutations = [
          'setInventory', 'setTradelog', 'setPokedex',
          'setPokemons', 'setUserHistory', 'setGuildHistory',
          'setVariables', 'setConnections', 'setUserMails', 'setSharedIPs'
        ];
        mutations.forEach(m => {
          try {
            if (m === 'setInventory') {
              this.$store.commit(m, { data: { data: null } });
            } else if (m === 'setPVPlogs') {
               this.$store.commit(m, { data: { data: null } });
            } else if (m === 'setUserMails') {
               this.$store.commit(m, { data: { data: null } });
            } else {
              this.$store.commit(m, null);
            }
          } catch(e) {}
        });
        try { this.$store.commit('setPVPlog', {data: null}); } catch(e) {}
      },

      fetchUserData() {
        const tab = (this.$route.params.tab || 'profile').toLowerCase();
        const lightweightTabs = ['pokedex', 'pokemon'];
        const needsFull = !lightweightTabs.includes(tab);

        // Check if we already have sufficient data
        // We use 'reg_date' as a marker for Full Data vs Header Data
        const hasData = !!this.userData;
        const isFullData = hasData && this.userData.reg_date;

        if (hasData) {
          if (!needsFull) {
            // We only need header data, and we already have some data (Header or Full).
            // Skip redundant fetch.
            return;
          }
          if (needsFull && isFullData) {
            // We need full data, and we already have full data.
            // Skip redundant fetch.
            return;
          }
        }

        if (needsFull) {
          this.emit('getUserData', {
            username: this.$route.params.username,
            server: this.currentServer
          })
        } else {
          this.emit('getUserHeader', {
            username: this.$route.params.username,
            server: this.currentServer
          })
        }
      },
      switchServer(serverName) {
        // Only update route. Watcher handles the rest.
        if (this.currentServer === serverName) return;

        this.isUpdatingServer = true; // Show loading immediately for better UX
        this.$router.replace({
          query: { ...this.$route.query, server: serverName }
        }).catch(()=>{});
      },
      openMacroDetails() {
        this.$router.push({
          name: 'MacroDetails',
          params: { username: this.userData.username },
          query: { server: this.currentServer || 'gold' }
        })
      },
      style() {
        return this.userData && this.userData.style ? btoa(this.userData.style) : ''
      },
      getServerVariant(server) {
        const s = server.toLowerCase()
        if (this.servers[s]) {
          return this.servers[s].colour
        }
        return 'secondary'
      },
      banTypeLabel(type) {
        const labels = {
          'P': 'Permanently Banned',
          'T': 'Temporarily Banned',
          'E': 'Trade Banned',
          'M': 'Quiet Banned',
          'A': 'MAC Banned',
          'X': 'PVP Banned',
          'S': 'Single-player Banned'
        };
        return labels[type] || 'Banned (' + type + ')';
      },
      capitalizeFirstLetter(string) {
        return string.charAt(0).toUpperCase() + string.slice(1)
      },
      formatNumber(v) {
        return v ? v.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') : '0'
      }
    }}
</script>

<style scoped>
/* Softened Transitions */
.fade-enter-active, .fade-leave-active {
  transition: all 0.25s ease-out;
}
.fade-enter {
  opacity: 0;
  transform: translateY(8px);
}
.fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Tab Transition effect - more subtle */
/deep/ .tab-pane {
  animation: slideUpSubtle 0.3s ease-out;
}

@keyframes slideUpSubtle {
  from {
    opacity: 0;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.user-dashboard-container {
  perspective: 1000px;
}

.dashboard-card {
  transition: all 0.3s ease;
}

.is-loading-server {
  filter: brightness(0.95);
  transform: scale(0.995);
  pointer-events: none;
}

.server-btn {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600 !important;
}

.server-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.server-btn:active {
  transform: translateY(0);
}

/* Enhanced Tab Visibility */
/deep/ .nav-tabs .nav-item .nav-link.active {
  color: #20a8d8 !important; /* CoreUI Primary Blue */
  font-weight: bold;
  position: relative;
  background: transparent;
  border: none;
  border-bottom: 3px solid #20a8d8;
}

/deep/ .nav-tabs .nav-item .nav-link {
  border: none;
  padding: 10px 15px;
  transition: all 0.2s ease;
}

/deep/ .nav-tabs {
  border-bottom: 1px solid rgba(255,255,255,0.1);
  margin-bottom: 15px;
}

.sortable-tab {
  cursor: pointer;
  transition: transform 0.2s ease;
}

.sortable-tab:active {
  cursor: pointer;
}

.drag-handle {
  font-size: 0.75rem;
  opacity: 0.3;
  transition: all 0.2s;
  cursor: grab;
  position: relative;
}

/* Invisible hitbox expansion - significantly larger for mobile */
.drag-handle::after {
  content: '';
  position: absolute;
  top: -25px;
  bottom: -25px;
  left: -25px;
  right: -25px;
}

.drag-handle:hover {
  opacity: 1;
  color: #20a8d8;
}

.ghost-tab {
  opacity: 0.4;
  background: rgba(32, 168, 216, 0.1) !important;
  border-bottom: 2px dashed #20a8d8 !important;
}
</style>
